import { Column, RowData } from '@tanstack/react-table';
import { Input } from '../../../Input';
import { ColumnFilterCombobox } from './ColumnFilterCombobox';
import { Fragment } from 'react';
import useI18n from '../../../../hooks/useI18n';
import { RangeFilterValue, getNextRangeFilterValue } from './rangeFilterUtils';

interface TableCellFilterProps<TData extends RowData, TValue> {
  columns: Column<TData, TValue>[];
}

export function CellFilter<TData, TValue>({
  columns,
}: TableCellFilterProps<TData, TValue>) {
  const { t } = useI18n();

  return (
    <div className='eye:flex eye:flex-col eye:gap-16'>
      {columns.map((column, index) => {
        const variant = column.columnDef.meta?.filterVariant ?? 'text';
        if (!column.getCanFilter() || !column.getIsVisible()) {
          return null;
        }
        const filterValue = column.getFilterValue();
        const filterOptions = column.columnDef.meta?.filterOptions;
        const label = column.columnDef.header?.toString();

        return (
          <Fragment key={index}>
            {variant === 'range' ? (
              <div className='eye:mt-12 eye:flex eye:gap-16'>
                <Input
                  label={`${label} ${String(t('from'))}`}
                  className='eye:bg-white'
                  type='number'
                  min={0}
                  placeholder={t('value')}
                  value={(filterValue as RangeFilterValue)?.[0] ?? ''}
                  onChange={(e) => {
                    column.setFilterValue((old: RangeFilterValue | undefined) =>
                      getNextRangeFilterValue(old, 0, e.target.value),
                    );
                  }}
                />
                <Input
                  label={`${label} ${String(t('to'))}`}
                  className='eye:bg-white'
                  type='number'
                  min={0}
                  placeholder={t('value')}
                  value={(filterValue as RangeFilterValue)?.[1] ?? ''}
                  onChange={(e) => {
                    column.setFilterValue((old: RangeFilterValue | undefined) =>
                      getNextRangeFilterValue(old, 1, e.target.value),
                    );
                  }}
                />
              </div>
            ) : variant.includes('select') ? (
              // A select filter without options has nothing to offer, so the
              // column shows no filter rather than failing the whole table.
              filterOptions ? (
                <ColumnFilterCombobox column={column} options={filterOptions} />
              ) : null
            ) : (
              <div className='eye:mt-12'>
                <Input
                  label={label}
                  className='eye:bg-white'
                  placeholder={t('value')}
                  value={(filterValue as string) ?? ''}
                  onChange={(e) => column.setFilterValue(e.target.value)}
                />
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
